import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup,
    IconButton, List, ListItem, ListItemText, Paper, TextField, Tooltip, Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Download as DownloadIcon, Assessment as AssessmentIcon } from '@mui/icons-material';
import { ExecutionOverview, StatusReport } from '../../../types/execution';
import executionService from '../../../services/executionService';
import { errorMessage, formatDate } from './executionUtils';

type Notify = (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;

interface ReportsViewProps {
    projectId: number;
    overview: ExecutionOverview;
    notify: Notify;
}

const fileNameFor = (projectName: string, report: StatusReport) =>
    `Seguimento_${projectName.replace(/[^\w-]+/g, '_')}_${report.report_date.replace(/-/g, '')}.pptx`;

const ReportsView: React.FC<ReportsViewProps> = ({ projectId, overview, notify }) => {
    const [reports, setReports] = useState<StatusReport[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [baselineIds, setBaselineIds] = useState<number[]>([]);
    const [busy, setBusy] = useState(false);
    const [toDelete, setToDelete] = useState<StatusReport | null>(null);

    const allBaselines = overview.workstreams.flatMap((ws) => ws.baselines.map((b) => ({ ...b, workstreamName: ws.name })));

    const loadReports = useCallback(async () => {
        try {
            setReports(await executionService.listStatusReports(projectId));
        } catch (error) {
            notify(errorMessage(error, 'Erro ao carregar relatórios'), 'error');
        }
    }, [projectId, notify]);

    useEffect(() => { loadReports(); }, [loadReports]);

    const openDialog = () => {
        setTitle(`Seguimento Semanal - ${overview.project.name}`);
        setBaselineIds([]);
        setDialogOpen(true);
    };

    const handleDownload = async (report: StatusReport) => {
        try {
            await executionService.downloadStatusReportPptx(report.id, fileNameFor(overview.project.name, report));
        } catch (error) {
            notify(errorMessage(error, 'Erro ao gerar o PowerPoint'), 'error');
        }
    };

    const handleCreate = async () => {
        try {
            setBusy(true);
            const report = await executionService.createStatusReport(projectId, {
                report_date: overview.report_date, title, baseline_ids: baselineIds,
            });
            setDialogOpen(false);
            await loadReports();
            notify('Relatório emitido', 'success');
            await handleDownload(report);
        } catch (error) {
            notify(errorMessage(error, 'Erro ao emitir relatório'), 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!toDelete) return;
        try {
            await executionService.deleteStatusReport(toDelete.id);
            await loadReports();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao excluir relatório'), 'error');
        } finally {
            setToDelete(null);
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 240px' }}>
                    Emitir um relatório congela os dados na data de referência ({formatDate(overview.report_date)}).
                    Ele pode ser baixado de novo depois, sem mudar.
                </Typography>
                <Button variant="contained" startIcon={<AssessmentIcon />} onClick={openDialog} sx={{ flexShrink: 0 }}>Emitir relatório</Button>
            </Box>

            <Paper variant="outlined">
                <List>
                    {reports.length === 0 && (
                        <ListItem><ListItemText secondary="Nenhum relatório emitido ainda." /></ListItem>
                    )}
                    {reports.map((report) => (
                        <ListItem
                            key={report.id}
                            divider
                            secondaryAction={(
                                <>
                                    <Tooltip title="Baixar PowerPoint">
                                        <IconButton onClick={() => handleDownload(report)}><DownloadIcon /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Excluir">
                                        <IconButton color="error" onClick={() => setToDelete(report)}><DeleteIcon /></IconButton>
                                    </Tooltip>
                                </>
                            )}
                        >
                            <ListItemText
                                primary={report.title}
                                secondary={`Data de corte ${formatDate(report.report_date)} · emitido em ${formatDate(report.created_at)}`}
                            />
                        </ListItem>
                    ))}
                </List>
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Emitir relatório de seguimento</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="normal" label="Título" value={title} onChange={(e) => setTitle(e.target.value)} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Data de corte: <b>{formatDate(overview.report_date)}</b> (altere no topo da aba Execução)</Typography>
                    {allBaselines.length > 0 && (
                        <Box mt={2}>
                            <Typography variant="subtitle2">Incluir planos anteriores (não vigentes)</Typography>
                            <FormGroup>
                                {allBaselines.map((baseline) => (
                                    <FormControlLabel
                                        key={baseline.id}
                                        label={`${baseline.workstreamName} — ${baseline.name}`}
                                        control={(
                                            <Checkbox
                                                checked={baselineIds.includes(baseline.id)}
                                                onChange={(e) => setBaselineIds((prev) => e.target.checked
                                                    ? [...prev, baseline.id]
                                                    : prev.filter((id) => id !== baseline.id))}
                                            />
                                        )}
                                    />
                                ))}
                            </FormGroup>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={busy || !title.trim()}>Emitir e baixar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
                <DialogTitle>Excluir relatório</DialogTitle>
                <DialogContent><Typography>Excluir o relatório de {formatDate(toDelete?.report_date)}?</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setToDelete(null)}>Cancelar</Button>
                    <Button color="error" onClick={handleDelete}>Excluir</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReportsView;
